import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { line, BANT_ROWS, BANT_ROW_TOPICS, Color, Size, TILE_ACTION_LABELS, bantRowRule, formatDate, formatDateTime, heldDraftPill, meetingAsked, t, type DealMeetingView, type DiscoveryCardAnswer, type ProgressStageRow, type StatusResolver } from '@bms2/shared';
import { Button, Checkbox, Pill, StatusPill } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { Icon } from '../../components/Icon';
import { HelpTopicsButton } from '../../components/modals/HelpModal';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useDateFormat } from '../../shell/ViewerZone';
import { DEALS_PATH, FILES_PATH, patch, post } from './api';
import { DealAuthorityList, type AuthorityRowView } from './DealAuthority';
import { DealBudget, type DealBudgetView } from './DealBudget';
import { DealTiming, type DealTimingView } from './DealTiming';
import { DealBomList, type BomListRow } from './DealBomList';
import { DealSectionNotes, type SectionNoteReplies } from './DealSectionNotes';
import type { TileRow } from './tileItems';
import { RecordUploadModal } from './RecordUploadModal';
import { RecordEventModal } from './RecordEventModal';
import { NestedTable, StageBlocked, StageCard, type StageCardRowView } from './StageCard';
import { DiscoveryCard } from './discovery/DiscoveryCard';
import styles from './DealPage.module.css';

export interface StageCardRowAnswer {
  subStage: string;
  label: string;
  placeholder: string;
  applicable: boolean;
  checked: boolean;
  notes: string | null;
  dueDate: string | null;
  systemDriven: boolean;
  /** The row's answer is a list the page carries, nested under it (decision 0332). */
  listKind?: 'bom' | 'rfq' | null;
  /** The row's answer is an uploaded file, in place of a note (decision 0595). */
  takesFile?: boolean;
  fileId?: string | null;
  fileName?: string | null;
  /** 🔒 The row's tagged notes, newest first (decision 0725); absent or null on a row that takes none. */
  sectionNotes?: readonly TileRow[] | null;
  /** 🔒 One link from an open system-driven Won row to where its act is done (decision 10745; T1013); absent where the place does not exist for this Deal. */
  link?: { route: string; label: string } | null;
}

export interface StageCardAnswer {
  stage: string;
  bant: boolean;
  counter: string;
  rows: StageCardRowAnswer[];
  canComplete: boolean;
  problems: string[];
  nextStage: string | null;
  /** The stage Back moves to, on the current card alone (decision 0331). */
  previousStage?: string | null;
  current?: boolean;
  /** A stage the Deal has entered takes ticks and notes whether or not it is current (decision 0331). */
  editable?: boolean;
  /** 🔒 False on the Intent card, whose rows carry no Due of their own (decision 0719). */
  takesDueDates?: boolean;
  /** 🔒 The Intent card's one Age in whole days, drawn in its head (decision 0719). */
  ageDays?: number | null;
  /** 🔒 Whether the Deal has entered the stage; Decision and Won draw only once it has (decision 1216). Absent reads as the ladder's state. */
  entered?: boolean;
  /** 🔒 When a finished stage ended, read on its folded line as Completed and a date (decision 1216). */
  completedAt?: string | null;
}

/** An RFQ as the RFQ Issued block lists it: what the retired RFQs tile listed (decision 0595). */
export interface RfqCardRow {
  id: string;
  rfqNumber: string;
  vendorName: string | null;
  issuedAt: string | null;
  responsesDueAt?: string | null;
  lineCount?: number;
  status: string;
  /** Every vendor the request went to, folded under the RFQ's own row (decision 0609). */
  vendors?: readonly { id: string; vendorName: string; status: string; sentAt: string | null; respondedAt: string | null }[];
  vendorsAsked?: number;
  vendorsAnswered?: number;
}


/** The newest Quote on the Deal as the Submit Quote row reads it (decision 0666): the printed number, and the displayed status member the Quote's own resolver draws. */
export interface LatestQuoteView {
  id: string;
  number: string;
  status: string;
  /** 🔒 A held Draft reads Pending Approval where it says so (T1195; decision 1861); the API always sends it, and a reply without one still draws the plain status. */
  approvalStatus: string;
}

/** 🔒 The newest Quote's pill reads Pending Approval on a held Draft, and Rejected on a rejected one (T1195; decision 1861); null draws the resolver's own. */
function heldResolve(quote: LatestQuoteView): StatusResolver | null {
  const held = heldDraftPill(quote.status, quote.approvalStatus);
  return held ? () => ({ label: held.text, colorGroup: held.group }) : null;
}

export interface DealStageCardProps {
  dealId: string;
  card: StageCardAnswer;
  /** The Deal's bills of materials, which the BOM Built block authors in place (decision 0595). */
  bomLists?: readonly BomListRow[];
  rfqs?: readonly RfqCardRow[];
  /** 🔒 Who on the buying side carries weight, drawn under the Authority row (decision 0618). */
  authority?: readonly AuthorityRowView[];
  /** 🔒 The Budget row's Target and Maximum with every pair saved (decision 0664). */
  budget?: DealBudgetView | null;
  /** 🔒 The Timing row's three dates (decision 0719). */
  timing?: DealTimingView | null;
  /** 🔒 The newest Quote, read inline on the Submit Quote row (decision 0666). */
  latestQuote?: LatestQuoteView | null;
  /** 🔒 Draft quote on the Submit Quote row: the same function the Quotes frame's + New Quote calls (decision 0666). */
  onDraftQuote?: (() => void) | null;
  seesCost?: boolean;
  /** False on a Lost Deal, whose lists are kept for reference and gain none (piece 18). */
  listsEditable?: boolean;
  /**
   * 🔒 `done` where the Deal is Won, by its stage alone (decisions 1466, 1512):
   * its bills of materials are kept for reference and the tile offers no Add,
   * Attach or Detach, while the card's other rows stay as they are.
   */
  bomLocked?: 'done' | null;
  /**
   * 🔒 False where the page's answer says the viewer holds no Deal write (decision
   * 1000; T365): the card then draws no tick, no note box, no list control, no Back
   * and no Mark Complete, because every one of them writes through a route that
   * refuses the viewer.
   */
  writes?: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError: (message: string) => void;
  /** A sentence to report after a write the blocks made, the Add BOM notice among them. */
  onNotice?: (message: string) => void;
  /** Opens the one RFQ modal, which the page owns; the block draws the control (decisions 0495, 0595). */
  onRfq?: () => void;
  /** The stage's own actions beside its button: Won and Mark Lost on the Decision card (decision 0595). */
  actions?: ReactNode;
  /** What the card says under its rows: the branch at Decision, with its clock. */
  note?: string | null;
  /**
   * 🔒 The ladder reads the stage done (decision 0723): the card's head carries the
   * tick and the card starts folded, unless the person opened it on this device.
   */
  complete?: boolean;
  /**
   * 🔒 Whether a finished card is shown, held by the page so a reload after a
   * write keeps it open (decision 1216); absent, the card holds it itself. Every
   * open of the page starts folded.
   */
  shown?: boolean;
  onShown?: (shown: boolean) => void;
  /** 🔒 The current card's whole days at its stage, read in its head (decision 1216); null on every other card. */
  daysAtStage?: number | null;
  /** The viewer, for the pencil a section note's author alone is offered (decision 0725). */
  personId?: string;
  /** The viewer's zone, so a section note's time reads in their own day (decision 0261). */
  timeZone?: string;
  /** The replies beneath each section note, through the page's one thread hook (decision 0671). */
  sectionReplies?: SectionNoteReplies;
  /** 🔒 The Discovery card's one shape, drawn as the Discovery stage card's body (decision 5450). */
  discovery?: DiscoveryCardAnswer | null;
  /** 🔒 What Set Meeting and Mark Intent Complete read on the Intent card (decision 6001); null on every other card. */
  meeting?: DealMeetingView | null;
}

/**
 * A gated stage's card (piece 19; figure 82): the one StageCard construction
 * over the Deal's sub-stage rows, Intent rendered as the BANT checklist. Each
 * tick or note writes the sub-stage row, which stamps lastMovedAt and leaves
 * enteredAt alone. 🔨 The stage ends on the rep's own Mark Complete button
 * through canCompleteStage, never on a condition the page notices; a
 * system-driven Won row has no checkbox. A completed stage's card takes the
 * same writes, so an earlier answer is corrected in place, and the current
 * card carries Back to the stage behind it (decision 0331).
 *
 * 🔒 The Evaluation card is where the evaluation is done (decision 0595,
 * amending 0332): BOM Built holds the Deal's bills of materials with Add BOM
 * and each name opening its own page, and RFQ Issued holds the RFQs with the
 * control that sends one. Negotiate Terms takes the negotiated terms document
 * in place of a note. Decision draws as a card with no rows, carrying Won,
 * Mark Lost and Back.
 */
export function DealStageCard({ dealId, card, bomLists = [], rfqs = [], authority = [], budget = null, timing = null, latestQuote = null, onDraftQuote = null, seesCost = false, listsEditable: listsOpen = true, bomLocked = null, writes = true, apiBaseUrl = '', onChanged, onError, onNotice, onRfq, actions, note = null, complete: stageComplete = false, shown, onShown, daysAtStage = null, personId = '', timeZone = 'UTC', sectionReplies, discovery = null, meeting = null }: DealStageCardProps) {
  const current = card.current ?? true;
  const editable = writes && (card.editable ?? current);
  const listsEditable = writes && listsOpen;
  const resolveStatus = useStatusResolver();
  const dateFormat = useDateFormat();
  const [shownHere, setShownHere] = useState(false);
  const isShown = shown ?? shownHere;
  /** The file-answered row an upload is for, with the files dropped on it. */
  const [uploading, setUploading] = useState<{ subStage: string; files: readonly File[] } | null>(null);
  /** Resolves true once the server took the write, so a row's quick entry clears only then (decision 0725). */
  const write = async (subStage: string, body: Record<string, unknown>): Promise<boolean> => {
    const answer = await patch(`${DEALS_PATH}/${dealId}/sub-stages/${subStage}`, body, apiBaseUrl);
    if (!answer.ok) {
      onError(answer.message);
      return false;
    }
    onChanged();
    return true;
  };
  /** 🔒 Decision 6001: the Intent card's Set Meeting window and Mark Intent Complete's ask, each mounted only while open. */
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const meets = card.stage === 'Intent' && meeting !== null;
  /** The completion's refusal, or null once it completes: the card writes it in its banner, the meeting ask inside its own frame (decision 3060). */
  const complete = async (salesCall = false): Promise<string | null> => {
    const answer = await post(`${DEALS_PATH}/${dealId}/complete-stage`, salesCall ? { salesCall: true } : {}, apiBaseUrl);
    if (!answer.ok) return answer.message;
    setAsking(false);
    onChanged();
    return null;
  };
  // 🔒 Mark Intent Complete asks for the meeting unless one is set or the sales call was ticked before (decision 6001).
  const pressComplete = () => (meets && meetingAsked(meeting) ? setAsking(true) : void complete().then((refused) => (refused === null ? undefined : onError(refused))));
  const back = async () => {
    const answer = await post(`${DEALS_PATH}/${dealId}/step-back`, {}, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  /**
   * The block under a list-backed row. 🔒 BOM Built is the bill-of-materials
   * authoring the tile used to carry (decisions 0565, 0573): one read-only table
   * per list under its own name, Add BOM at its foot, each name a link to the
   * list's own page, which is where a line is typed. RFQ Issued is the Deal's
   * RFQ list with the control that raises one (decision 0495). Both start
   * unfolded, because a control behind a press is a control nobody finds.
   */
  const nestedOf = (kind: 'bom' | 'rfq') =>
    kind === 'bom'
      ? {
          label: t('deal.card.boms'),
          count: bomLists.length,
          startOpen: true,
          content: (
            <DealBomList
              dealId={dealId}
              rows={bomLists}
              editable={listsEditable && bomLocked === null}
              lockReason={bomLocked ?? (listsOpen ? null : 'lost')}
              seesCost={seesCost}
              apiBaseUrl={apiBaseUrl}
              onSaved={(message) => { onChanged(); onNotice?.(message); }}
            />
          ),
        }
      : {
          label: t('deal.card.rfqs'),
          count: rfqs.length,
          startOpen: true,
          content: (
            <>
              <RfqRows rows={rfqs} />
              {listsEditable && onRfq ? (
                <div className={styles.cardControl} data-stage-question-action="RFQIssued">
                  <Button size={Size.Small} color={Color.Navy} text={[TILE_ACTION_LABELS.newRfq]} actions={[onRfq]} />
                </div>
              ) : null}
            </>
          ),
        };
  const rows: StageCardRowView[] = card.rows.map((row) => ({
    key: row.subStage,
    label: row.label,
    placeholder: row.placeholder,
    checked: row.checked,
    notes: row.notes,
    applicable: row.applicable,
    derived: row.systemDriven,
    derivedText: row.checked ? t('deal.card.done') : t('deal.card.systemDriven'),
    dueDate: row.dueDate,
    // 🔒 On the Intent card each row says in one line what turns it green (decision 0724).
    rule: card.bant ? ruleText(row.subStage) : null,
    /*
     * 🔒 A row a person types on keeps its notes as tagged Deal notes (decision 0725):
     * the composer adds the next, and every note stands beneath it in the Internal
     * Notes tile's own list, three tall and scrolling inside (decision 0950).
     */
    ...(row.sectionNotes
      ? {
          notes: null,
          section: (
            <DealSectionNotes
              dealId={dealId}
              subStage={row.subStage}
              notes={row.sectionNotes}
              personId={personId}
              timeZone={timeZone}
              apiBaseUrl={apiBaseUrl}
              {...(sectionReplies ? { replies: sectionReplies } : {})}
              onChanged={onChanged}
              onError={onError}
            />
          ),
        }
      : {}),
    // 🔒 An open system-driven Won row carries one link to where its act is done; the banner above stays link-free (decision 10745; T1013).
    ...(row.link ? { extra: <Link className={styles.quoteLink} to={row.link.route} data-deal-system-link={row.subStage}>{row.link.label}</Link> } : {}),
    nested: row.listKind ? nestedOf(row.listKind) : null,
    /**
     * 🔒 The Authority row carries the people who decide beneath its own note box
     * (decision 0618). The note box stays as the summary of the whole committee,
     * which is the answer to the owner's *per person or globally*: both.
     */
    ...(row.subStage === 'Authority'
      ? {
          extra: (
            <DealAuthorityList
              dealId={dealId}
              rows={authority}
              editable={editable && listsEditable}
              apiBaseUrl={apiBaseUrl}
              onChanged={onChanged}
              onError={onError}
            />
          ),
        }
      : {}),
    /**
     * 🔒 The Budget row answers with a Target and a Maximum in place of its note
     * box (decision 0664); the tick follows the pair, so the row draws no
     * checkbox. Keyed to the newest saved row, so a save starts the inputs afresh
     * from what was saved. A row marked N/A draws neither input.
     */
    ...(row.subStage === 'Budget' && budget && row.applicable
      ? {
          answer: (
            <DealBudget
              key={`budget-${budget.history[0]?.id ?? 'none'}`}
              dealId={dealId}
              budget={budget}
              editable={editable && listsEditable}
              apiBaseUrl={apiBaseUrl}
              onChanged={onChanged}
              onError={onError}
            />
          ),
        }
      : {}),
    /**
     * 🔒 The Timing row answers with its three dates in place of its note box
     * (decision 0719); the tick follows the Expected quote, so the row draws no
     * checkbox. Keyed to the standing dates, so a save starts the inputs afresh
     * from what was saved. A row marked N/A draws no input.
     */
    ...(row.subStage === 'Timing' && timing && row.applicable
      ? {
          answer: (
            <DealTiming
              key={`timing-${timing.current.expectedQuoteDate ?? ''}-${timing.current.expectedStartDate ?? ''}-${timing.current.expectedFinishDate ?? ''}`}
              dealId={dealId}
              timing={timing}
              editable={editable && listsEditable}
              apiBaseUrl={apiBaseUrl}
              onChanged={onChanged}
              onError={onError}
            />
          ),
        }
      : {}),
    /**
     * 🔒 The Submit Quote row carries Draft quote and the newest Quote's status
     * beneath its note box (decision 0666). The button is the Quotes frame's own
     * + New Quote, handed down; a sent Quote ticks the row with
     * its number (decision 10742). A row marked N/A draws neither.
     */
    ...(row.subStage === 'SubmitQuote' && row.applicable && (latestQuote || (onDraftQuote && editable && listsEditable))
      ? {
          extra: (
            <div className={styles.quoteRow} data-deal-quote="SubmitQuote">
              {latestQuote ? (
                <span className={styles.quoteStatus} data-deal-quote-latest={latestQuote.id}>
                  <span className={styles.quoteLabel}>{t('deal.quote.latest')}</span>
                  <Link className={styles.quoteLink} to={`/quotes/${latestQuote.id}`}>{latestQuote.number}</Link>
                  <StatusPill entityType="QuoteStatus" statusValue={latestQuote.status} resolve={heldResolve(latestQuote) ?? resolveStatus} size={Size.Small} />
                </span>
              ) : null}
              {onDraftQuote && editable && listsEditable ? (
                <span data-deal-quote-action="draft">
                  <Button size={Size.Small} color={Color.Navy} text={[t('deal.quote.draft')]} actions={[onDraftQuote]} />
                </span>
              ) : null}
            </div>
          ),
        }
      : {}),
    /**
     * 🔒 The proposal report stands on the Proposal Reviewed row (decision 6481; T2865, the owner's answer A4a):
     * the report the Discovery card names, its name opening the file and its version beside it, or a line saying
     * none is made yet. 🔨 It ticks nothing: the row still asks whether the prospect reviewed it.
     */
    ...(row.subStage === 'ProposalReviewed' && discovery
      ? {
          extra: (
            <div className={styles.quoteRow} data-deal-proposal-report={discovery.report ? discovery.report.fileId : 'none'}>
              {discovery.report ? (
                <span className={styles.quoteStatus}>
                  <span className={styles.quoteLabel}>{t('deal.proposal.report')}</span>
                  <a className={styles.quoteLink} href={`${apiBaseUrl}${FILES_PATH}/${discovery.report.fileId}/stream`} target="_blank" rel="noreferrer">{discovery.report.filename}</a>
                  <span className={styles.quoteLabel}>{line(t('makeReport.version'), discovery.report.version)}</span>
                </span>
              ) : (
                <span className={styles.quoteLabel}>{t('deal.proposal.none')}</span>
              )}
            </div>
          ),
        }
      : {}),
    // 🔒 The file answers the row, or a typed note with the tick does (decision 2091, amending 0595): the row keeps both.
    file: row.takesFile
      ? {
          orNote: true,
          fileId: row.fileId ?? null,
          fileName: row.fileName ?? null,
          href: row.fileId ? `${apiBaseUrl}${FILES_PATH}/${row.fileId}/stream` : null,
          uploadLabel: t('deal.card.upload'),
          uploadHint: t('deal.card.uploadHint'),
          onFiles: (files) => setUploading({ subStage: row.subStage, files }),
          onRemove: () => void write(row.subStage, { fileId: null }),
        }
      : null,
  }));
  /** 🔒 The head's line (decision 1216): Completed and its date on a finished card, n of n done, the days at this stage on the current one, and the lists a folded card holds. */
  const applicableRows = card.rows.filter((row) => row.applicable);
  // 🔒 A card with no applicable row draws no 0 of 0 done (decision 6702).
  const counterLabel = applicableRows.length === 0 ? '' : line(String(applicableRows.filter((row) => row.checked).length), t('deal.card.of'), String(applicableRows.length), t('deal.card.done'));
  const finished = stageComplete && !current;
  /**
   * 🔒 A card other than the current one with nothing recorded folds to Intent · nothing recorded yet, opening on a
   * press (decision 6702, amending 1216). The current card, the one being filled in now, stays open whatever it holds.
   * A card drawn by the Discovery card's own sections is judged by its rows alone and never called empty.
   */
  const quiet = !current && card.stage !== 'Discovery' && !stageHoldsSomething(card, { bomLists: bomLists.length, rfqs: rfqs.length, authority: authority.length, budget, timing, latestQuote, meeting });
  const completedLead = finished && card.completedAt ? line(t('deal.card.completed'), formatDate(card.completedAt, timeZone, dateFormat)) : null;
  const daysText = current && daysAtStage !== null && card.ageDays == null && !note
    ? (daysAtStage === 1 ? t('deal.card.oneDayAtStage') : line(String(daysAtStage), t('deal.card.daysAtStage')))
    : null;
  const listCounts = [
    ...(card.rows.some((row) => row.listKind === 'bom') && bomLists.length > 0 ? [line(String(bomLists.length), t(bomLists.length === 1 ? 'deal.card.bomOne' : 'deal.card.bomMany'))] : []),
    ...(card.rows.some((row) => row.listKind === 'rfq') && rfqs.length > 0 ? [line(String(rfqs.length), t(rfqs.length === 1 ? 'deal.card.rfqOne' : 'deal.card.rfqs'))] : []),
  ];
  return (
    <>
      <StageCard
        stage={card.stage}
        subtitle={card.bant ? t('deal.card.bant') : !current && editable && !finished ? t('deal.card.earlier') : null}
        counterLabel={counterLabel}
        lead={completedLead}
        trail={daysText ? [daysText] : []}
        foldedTrail={listCounts}
        badge={current ? <Pill text={t('deal.progress.now')} foreground="var(--navy-primary)" background="var(--selection-bg)" size={Size.Small} /> : null}
        backInHead
        // 🔒 The Discovery stage's body is the Discovery card, bound to this Deal and this stage by its answer (decision 5450).
        // 🔒 Its writes are the Deal's own and not the stage card's: Discovery is not locked behind Intent (decision 5461).
        body={card.stage === 'Discovery' && discovery ? <DiscoveryCard card={discovery} apiBaseUrl={apiBaseUrl} onChanged={onChanged} onError={onError} {...(onNotice ? { onNotice } : {})} /> : null}
        counter={card.counter}
        state={current ? 'current' : editable ? 'done' : 'todo'}
        rows={rows}
        editable={editable}
        notesLabel={t('deal.card.notes')}
        // 🔨 A row with a history sends its tick alone: its words are written by its box's own blur, as a tagged note (decision 0725).
        onCheck={(key, checked, notes) => void write(key, card.rows.find((row) => row.subStage === key)?.sectionNotes ? { checked } : { checked, notes })}
        onNotes={(key, notes) => write(key, { notes })}
        // 🔒 The Intent card is one meeting with one Age, so its rows draw no Due (decision 0719); the other cards keep theirs.
        {...(card.takesDueDates === false ? {} : { onDue: (key: string, due: string | null) => void write(key, { dueDate: due }) })}
        {...(card.ageDays === null || card.ageDays === undefined ? {} : { age: line(t('deal.card.age'), card.ageDays === 1 ? t('deal.card.oneDay') : `${card.ageDays} ${t('deal.card.days')}`) })}
        onApplicable={(key, applicable) => void write(key, { applicable })}
        onBack={writes && current && card.previousStage ? () => void back() : null}
        backLabel={card.previousStage ? line(t('deal.card.back'), card.previousStage) : null}
        data={{ 'data-stage-card-bant': card.bant ? 'true' : 'false' }}
        // 🔒 A finished stage folds to one line on every open and the current one draws in full; nothing is remembered (decision 1216, amending 0723).
        fold={finished || quiet ? { folded: !isShown, onToggle: () => (onShown ? onShown(!isShown) : setShownHere(!isShown)) } : null}
        empty={quiet ? t('deal.card.nothingRecorded') : null}
        complete={stageComplete}
        // 🔒 The Intent card's head carries one help control opening a topic per row, in row order (decision 0724).
        help={card.bant ? <HelpTopicsButton titleKey="help.dealIntent.title" topics={BANT_ROWS.filter((key) => card.rows.some((row) => row.subStage === key)).map((key) => BANT_ROW_TOPICS[key])} /> : null}
        {...(note ? { note } : {})}
        foot={writes && current && (card.nextStage || actions) ? (
          <>
            {card.nextStage && !card.canComplete ? <StageBlocked problems={card.problems} prefix={t('deal.card.blocked')} /> : null}
            {actions}
            {/* 🔒 Set Meeting books the Discovery visit as an event on the Deal with its people and its site (decision 6001). */}
            {meets && meeting ? (
              <span className={styles.meeting} data-deal-meeting={meeting.set ? 'set' : 'none'}>
                {meeting.set ? <span className={styles.meetingLine} data-deal-meeting-booked={meeting.set.eventId}>{line(t('deal.meeting.booked'), formatDateTime(meeting.set.startsAt, timeZone, dateFormat))}</span> : null}
                <Button size={Size.Medium} color={Color.Inactive} text={[t('deal.meeting.set')]} actions={[() => setMeetingOpen(true)]} />
              </span>
            ) : null}
            {card.nextStage ? <Button size={Size.Medium} color={Color.Navy} text={[line(t('deal.card.mark'), card.stage, t('deal.card.complete'))]} actions={[pressComplete]} disabled={!card.canComplete} /> : null}
          </>
        ) : null}
      />
      {asking && meeting ? <MeetingAsk onSetMeeting={() => setMeetingOpen(true)} onComplete={() => complete(true)} onCancel={() => setAsking(false)} /> : null}
      {/* 🔨 Keyed to the Deal it books for and mounted only while open, so a reopened window starts from the Deal's own people and site. */}
      {meetingOpen && meeting ? (
        <RecordEventModal
          key={`deal-meeting-${dealId}`}
          open
          recordType="Deal"
          recordId={dealId}
          ownerPersonId={personId}
          timeZone={timeZone}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setMeetingOpen(false)}
          onSaved={() => { setAsking(false); onChanged(); }}
          onError={onError}
          preset={{ subject: meeting.subject, siteId: meeting.siteId, people: meeting.people }}
        />
      ) : null}
      {/* 🔨 Keyed to the row it answers: a modal mounted and hidden reads its files once, and a second row would upload against the first one's draft. */}
      {/* 🔒 Decision 6090: the window closes only through its own Done or Stop, never on the per-file report, so every file picked answers the row in turn. */}
      <RecordUploadModal
        key={uploading ? `deal-file-${uploading.subStage}` : 'deal-file-closed'}
        open={uploading !== null}
        recordType="Deal"
        recordId={dealId}
        apiBaseUrl={apiBaseUrl}
        initialFiles={uploading?.files ?? []}
        onUploaded={async (file) => {
          if (!uploading) return;
          const answer = await patch(`${DEALS_PATH}/${dealId}/sub-stages/${uploading.subStage}`, { fileId: file.id }, apiBaseUrl);
          if (!answer.ok) throw new Error(answer.message);
        }}
        onCancel={() => setUploading(null)}
        onSaved={onChanged}
      />
    </>
  );
}

/** The Intent row's completion line through the one lookup, or null for a row that carries none (decision 0724). */
function ruleText(subStage: string): string | null {
  const key = bantRowRule(subStage);
  return key ? t(key) : null;
}

/**
 * 🔒 The RFQ Issued block, one row per request with its vendors folded under it
 * (decision 0609, amending 0595). The row says the number, the status, how many
 * vendors were asked and how many answered, and when the answer is due; opening
 * it draws those vendors as the table the block used to draw for every RFQ at
 * once. Product owner, 2026-09-21: *"Rows by default with the table folded under
 * each."*
 *
 * 💰 The block carried one vendor per RFQ, the first response's, so a request
 * sent to four vendors read on the Deal as a request sent to one.
 */
function RfqRows({ rows }: { rows: readonly RfqCardRow[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (rows.length === 0) return <p className={styles.cardEmpty}>{t('deal.card.noRfqs')}</p>;
  return (
    <ul className={styles.foldRows}>
      {rows.map((row) => {
        const expanded = open[row.id] === true;
        const asked = row.vendorsAsked ?? row.vendors?.length ?? 0;
        const answered = row.vendorsAnswered ?? 0;
        return (
          <li key={row.id} className={styles.foldRow} data-rfq-row={row.id} data-rfq-row-expanded={expanded ? 'true' : 'false'}>
            <div className={styles.foldHead}>
              <span className={styles.foldName}>{row.rfqNumber}</span>
              <span className={styles.foldFact}>{row.status}</span>
              <span className={styles.foldFact} data-rfq-vendor-count={row.id}>{line(String(asked), t('deal.card.rfqAsked'), '·', String(answered), t('deal.card.rfqAnswered'))}</span>
              {row.responsesDueAt ? <span className={styles.foldFact}>{line(t('deal.card.colDue'), row.responsesDueAt)}</span> : null}
              <button
                type="button"
                className={styles.foldButton}
                aria-expanded={expanded ? 'true' : 'false'}
                onClick={() => setOpen((held) => ({ ...held, [row.id]: !expanded }))}
                data-rfq-fold={row.id}
              >
                {expanded ? t('deal.card.hideVendors') : t('deal.card.showVendors')}
                <Icon meaning={expanded ? 'collapse' : 'expand'} size="inline" />
              </button>
            </div>
            {expanded ? (
              <NestedTable
                columns={[{ key: 'vendor', label: t('deal.card.colVendor') }, { key: 'sent', label: t('deal.card.colSent') }, { key: 'answered', label: t('deal.card.colAnswered') }, { key: 'status', label: t('deal.card.colStatus') }]}
                rows={(row.vendors ?? []).map((vendor) => ({ id: vendor.id, cells: { vendor: vendor.vendorName, sent: vendor.sentAt, answered: vendor.respondedAt, status: vendor.status } }))}
                empty={t('deal.card.noVendors')}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 🔒 Mark Intent Complete's ask (decision 6001; the owner's answer D10a): with no
 * meeting set it asks for one. Set Meeting opens the event window over it, and
 * ticking *It came up on the sales call* lets the stage complete without one,
 * the tick kept as the Intent note on the Deal. 🔨 The tick is a value the
 * server keeps, so the ask is a form and draws through the Form frame (decision
 * 3060): a refusal lands inside it, and Cancel after a tick asks first.
 */
function MeetingAsk({ onSetMeeting, onComplete, onCancel }: { onSetMeeting: () => void; onComplete: () => Promise<string | null>; onCancel: () => void }) {
  const [salesCall, setSalesCall] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const act = async () => {
    setRefusal(null);
    setRefusal(await onComplete());
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('deal.meeting.askTitle')}
      dataForm="deal-meeting-ask"
      typed={salesCall}
      onCancel={onCancel}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('deal.meeting.complete'), disabled: !salesCall, disabledReason: t('deal.meeting.needSalesCall'), onAct: act }}
    >
      <p className={styles.meetingLine}>{t('deal.meeting.askBody')}</p>
      <span data-deal-meeting-ask-set="true">
        <Button size={Size.Medium} color={Color.Navy} text={[t('deal.meeting.set')]} actions={[onSetMeeting]} />
      </span>
      <Checkbox label={t('deal.meeting.salesCall')} checked={salesCall} onChange={setSalesCall} name="deal-meeting-sales-call" />
    </FormFrame>
  );
}

/**
 * A stage the Deal has not reached reads off the ladder's own rows (figure
 * 82 scrolls every card): its sub-stages waiting under 0/n. Nothing on it is
 * edited until the Deal enters the stage.
 *
 * 🔒 A Lost Deal's cards all read, and its Authority row still draws the people
 * who decided, read-only (decision 0967): the page passes `authority` there and
 * nowhere else, so who decided on the buying side stays on the Deal somebody
 * reviews afterwards.
 */
export function DealStageReadCard({ row, dealId = '', authority = null }: { row: ProgressStageRow; dealId?: string; authority?: readonly AuthorityRowView[] | null }) {
  const applicable = row.subStages.filter((sub) => sub.applicable);
  const done = applicable.filter((sub) => sub.completed).length;
  // 🔒 A read card with nothing recorded folds to its one line and opens on a press (decision 6702); nothing is remembered.
  const [shown, setShown] = useState(false);
  const quiet = !row.subStages.some((sub) => sub.completed) && (authority ?? []).length === 0;
  const rows: StageCardRowView[] = row.subStages.map((sub) => ({
    key: sub.subStage, label: sub.shortName, placeholder: '', checked: sub.completed, notes: null, applicable: sub.applicable, derived: false,
    ...(sub.subStage === 'Authority' && authority
      ? { extra: <DealAuthorityList dealId={dealId} rows={authority} editable={false} onChanged={() => undefined} onError={() => undefined} /> }
      : {}),
  }));
  return (
    <StageCard
      stage={row.stage}
      // 🔒 No 0/0 (decision 6702): a stage with no applicable step draws no counter.
      counter={applicable.length === 0 ? '' : `${done}/${applicable.length}`}
      // 🔒 The read-only card counts as the live one does, n of n done (decision 1216; T979).
      counterLabel={applicable.length === 0 ? '' : line(String(done), t('deal.card.of'), String(applicable.length), t('deal.card.done'))}
      state={row.state === 'done' ? 'done' : 'todo'}
      rows={rows}
      editable={false}
      notesLabel={t('deal.card.notes')}
      empty={quiet ? t('deal.card.nothingRecorded') : null}
      emptyRow={t('deal.card.notRecorded')}
      fold={quiet ? { folded: !shown, onToggle: () => setShown(!shown) } : null}
    />
  );
}

/** What a stage card's rows and the lists beside them hold, for the one question of whether anything is recorded. */
export interface StageHoldings {
  bomLists: number;
  rfqs: number;
  authority: number;
  budget?: DealBudgetView | null;
  timing?: DealTimingView | null;
  latestQuote?: LatestQuoteView | null;
  meeting?: DealMeetingView | null;
}

/**
 * 🔒 Whether a stage card holds anything a person recorded (decision 6702): a tick, a note, a due date, a file, a
 * tagged note, a list under its row, the Authority people, a saved budget or timing date, the newest Quote on Submit
 * Quote, or the Intent meeting. A row marked N/A records nothing by that mark alone.
 */
export function stageHoldsSomething(card: Pick<StageCardAnswer, 'rows'>, held: StageHoldings): boolean {
  return card.rows.some((row) => row.checked
    || Boolean(row.notes)
    || Boolean(row.dueDate)
    || Boolean(row.fileId)
    || (row.sectionNotes?.length ?? 0) > 0
    || (row.listKind === 'bom' && held.bomLists > 0)
    || (row.listKind === 'rfq' && held.rfqs > 0)
    || (row.subStage === 'Authority' && held.authority > 0)
    || (row.subStage === 'Budget' && Boolean(held.budget?.current))
    || (row.subStage === 'Timing' && Boolean(held.timing && Object.values(held.timing.current).some(Boolean)))
    || (row.subStage === 'SubmitQuote' && Boolean(held.latestQuote)))
    || Boolean(held.meeting?.set);
}
